import type { DRDaySummary, PickemItem, SquaresMyPick } from '../api/getMyGames';
import type { BingoMyGame } from './lobbyMyGamesBingo';
import type { SurvivorMyGame } from './lobbyMyGamesSurvivor';
import { dailyRevealProductName } from './dailyRevealProductName';

/**
 * Game-type filter for the My Games "Recent" tab (labelled "Past Results" until the tabs were
 * folded into the lobby). The `Past*` symbol names are kept so the module reads consistently
 * with `PastResultItem`, which is what the data actually is: results of finished contests.
 *
 * Daily Reveal splits per sport (`daily_reveal:NFL`) because each sport is a separately branded
 * product to the player — Gridiron Draw, Big League Draw, Full Court Draw. Bare `daily_reveal`
 * still matches every sport.
 */
export type PastGameFilter =
  | 'all'
  | 'daily_reveal'
  | `daily_reveal:${string}`
  | 'pickem'
  | 'squares'
  | 'survivor'
  | 'bingo';

export type PastGameFilterOption = { id: PastGameFilter; label: string };

/** Filters for the game types that carry one fixed player-facing name. */
export const STATIC_PAST_GAME_FILTERS: PastGameFilterOption[] = [
  { id: 'pickem', label: "Pick'em" },
  { id: 'squares', label: 'Squares' },
  { id: 'survivor', label: 'Survivor' },
  // "Bingo" is IP; Game Grid is DK's name for it, and it is what every other surface shows.
  { id: 'bingo', label: 'Game Grid' },
];

export type PastResultItem =
  | { kind: 'daily_reveal'; sortAt: number; day: DRDaySummary; sport: string }
  | { kind: 'pickem'; sortAt: number; pickem: PickemItem }
  | { kind: 'squares'; sortAt: number; pick: SquaresMyPick }
  | { kind: 'survivor'; sortAt: number; game: SurvivorMyGame }
  | { kind: 'bingo'; sortAt: number; game: BingoMyGame };

/** Sport codes reach us from several endpoints in mixed case; the filter id is the upper form. */
export function dailyRevealSportFilterId(sport: string | null | undefined): PastGameFilter {
  return `daily_reveal:${String(sport ?? '')
    .trim()
    .toUpperCase()}`;
}

/**
 * Chips for the Recent tab. One Daily Reveal chip per sport actually present in the results,
 * so a player who only plays Gridiron Draw is not offered three near-identical filters.
 */
export function buildPastGameFilters(items: PastResultItem[]): PastGameFilterOption[] {
  const drByFilterId = new Map<PastGameFilter, string>();
  for (const item of items) {
    if (item.kind !== 'daily_reveal') continue;
    drByFilterId.set(dailyRevealSportFilterId(item.sport), dailyRevealProductName(item.sport));
  }

  const dailyRevealFilters = [...drByFilterId.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return [{ id: 'all', label: 'All' }, ...dailyRevealFilters, ...STATIC_PAST_GAME_FILTERS];
}

/**
 * Section order for grouped views, matching the order Upcoming and Live render their sections
 * so a player moving between tabs finds the same game type in the same place. Daily Reveal is
 * not listed: it contributes one section per branded product and always leads.
 */
const PAST_SECTION_ORDER: Array<{ kind: PastResultItem['kind']; label: string }> = [
  { kind: 'squares', label: 'Squares' },
  { kind: 'survivor', label: 'Survivor' },
  { kind: 'bingo', label: 'Game Grid' },
  { kind: 'pickem', label: "Pick'em" },
];

export type PastResultSection = { label: string; items: PastResultItem[] };

/**
 * Split finished results into the same labelled sections Upcoming and Live use.
 *
 * Recent used to be one chronological list across every game type, which left Daily Reveal
 * cards unlabelled — a player could not tell a Gridiron Draw result from a Big League Draw one.
 * Items stay newest-first WITHIN a section; the sections themselves follow the running tabs'
 * order rather than the clock.
 */
export function groupPastResultItems(items: PastResultItem[]): PastResultSection[] {
  const dailyRevealByLabel = new Map<string, PastResultItem[]>();
  for (const item of items) {
    if (item.kind !== 'daily_reveal') continue;
    const label = dailyRevealProductName(item.sport);
    const bucket = dailyRevealByLabel.get(label);
    if (bucket) bucket.push(item);
    else dailyRevealByLabel.set(label, [item]);
  }

  const sections: PastResultSection[] = [...dailyRevealByLabel.entries()].map(
    ([label, sectionItems]) => ({ label, items: sectionItems })
  );

  for (const { kind, label } of PAST_SECTION_ORDER) {
    const sectionItems = items.filter(item => item.kind === kind);
    if (sectionItems.length > 0) sections.push({ label, items: sectionItems });
  }

  return sections;
}

function toSortTime(isoOrDate: string | undefined | null): number {
  if (!isoOrDate) return 0;
  // Date-only (YYYY-MM-DD) sorts as start-of-day UTC
  const t = new Date(isoOrDate).getTime();
  return Number.isFinite(t) ? t : 0;
}

export function buildPastResultItems(input: {
  dailyRevealDays: DRDaySummary[];
  /**
   * Sport of the contest that owns each Daily Reveal day, keyed by day id. A day carries no
   * sport of its own, and Recent spans every contest the player entered, so the caller resolves
   * it from the owning contest. Optional: callers written before the per-sport split still
   * compile, and an unmapped day falls back to the default product name.
   */
  dailyRevealSportByDayId?: Map<string, string>;
  pickems: PickemItem[];
  squares: SquaresMyPick[];
  /** Optional so existing callers/tests that predate Survivor keep compiling. */
  survivor?: SurvivorMyGame[];
  /** Optional for the same reason — callers written before Game Grid still compile. */
  bingo?: BingoMyGame[];
}): PastResultItem[] {
  const items: PastResultItem[] = [];

  for (const day of input.dailyRevealDays) {
    items.push({
      kind: 'daily_reveal',
      sortAt: toSortTime(day.gameDate),
      day,
      sport: input.dailyRevealSportByDayId?.get(day.id) ?? '',
    });
  }

  for (const pickem of input.pickems) {
    const start =
      (pickem.start as string | undefined) ??
      (pickem.start_utc_timestamp
        ? new Date((pickem.start_utc_timestamp as number) * 1000).toISOString()
        : null);
    items.push({
      kind: 'pickem',
      sortAt: toSortTime(start),
      pickem,
    });
  }

  for (const pick of input.squares) {
    items.push({
      kind: 'squares',
      sortAt: toSortTime(pick.start_time_utc ?? null),
      pick,
    });
  }

  for (const game of input.survivor ?? []) {
    // Sorted on the contest's own start: a season-long contest has no single "result date",
    // and its start is the one timestamp every Survivor contest carries.
    items.push({
      kind: 'survivor',
      sortAt: toSortTime(game.startUtc),
      game,
    });
  }

  for (const game of input.bingo ?? []) {
    // Sorted on the contest's own start, like Survivor: a Bingo card settles tile by tile across
    // a whole slate, so there is no single "result date" — the start is the one timestamp every
    // contest carries.
    items.push({
      kind: 'bingo',
      sortAt: toSortTime(game.startUtc),
      game,
    });
  }

  // Newest first; stable tie-break by kind then id for deterministic UI
  return items.sort((a, b) => {
    if (b.sortAt !== a.sortAt) return b.sortAt - a.sortAt;
    if (a.kind !== b.kind) return a.kind.localeCompare(b.kind);
    return pastItemKey(a).localeCompare(pastItemKey(b));
  });
}

export function filterPastResultItems(
  items: PastResultItem[],
  filter: PastGameFilter
): PastResultItem[] {
  if (filter === 'all') return items;
  if (filter.startsWith('daily_reveal:')) {
    return items.filter(
      item => item.kind === 'daily_reveal' && dailyRevealSportFilterId(item.sport) === filter
    );
  }
  return items.filter(item => item.kind === filter);
}

export function pastItemKey(item: PastResultItem): string {
  if (item.kind === 'daily_reveal') return `dr-${item.day.id}`;
  if (item.kind === 'pickem') return `pk-${item.pickem.pickem_id}`;
  if (item.kind === 'survivor') return `sv-${item.game.pickemId}`;
  if (item.kind === 'bingo') return `bg-${item.game.pickemId}`;
  return `sq-${item.pick.squares_contest_id}`;
}
